import React, { useContext } from "react";
import styled, { useTheme } from "styled-components";
import { OpenInNewIcon } from "common/Icons/Icons";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid";
import useGeneral from "features/tradingTerminal/General/useGeneral";
import Link from "common/Typography/Link";
import Flex from "common/Boxes/Flex";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import Title from "common/Typography/Title";
import {
  Arrow,
  FormDivider,
  FormFieldTitle,
} from "features/tradingTerminal/common/styles";
import { ErrorText } from "features/bots/common/styles";
import Tooltip from "common/Tooltips/Tooltip";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context.tsx";
import SignalAmountSelector from "features/tradingTerminal/General/SignalAmountSelector.tsx";
import SubGroupSelector from "features/tradingTerminal/General/SubGroupSelector.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import AutoSizeText from "common/Text/AutoSizeText.tsx";
import { ChevronDown, ExternalLink } from "lucide-react";
import LookIcon from "common/Icons/LookIcon";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook";
import { getNewLookTokens } from "common/NewLook/tokens.ts";
import DirectionFieldsRow from "common/Grids/DirectionFieldsRow";

const General = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);
  const theme = useTheme();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const {
    accountSelector,
    accountError,
    symbolSelector,
    symbolError,
    accountSelectorTitle,
    symbolSelectorTitle,
    amountInput,
    amountInputTitle,
    quantityInput,
    quantityInputTitle,
    openSymbolDrawer,
    showExistingAmount,
    onAddExistingAmountClick,
    onRemoveExistingAmountClick,
    isLoading,
    isFetching,
    longShort,
    leverage,
    cancelOnTp,
    tradeType,
    showTradeType,
    advancedExpanded,
    setAdvancedExpanded,
    onExistingQuantityClick,
    confirmModalContextHolder,
    showGeneralOptions,
    maxNumTradesForSymbolError,
    marginTypeErrorMessage,
    multiplierErrorMessage,
    marginErrorReason,
    amountValidationMessage,
    invalidTradeAmount,
    leverageWarningModal,
  } = useGeneral();
  const { isSignal } = useContext(TradingTerminalContext)!;
  // New look: the label role (12.5/16, 500, secondary).
  const autoSizeTitleStyle = theme.isNewLook
    ? {
        ...AUTO_SIZE_TITLE_STYLE,
        fontSize: 12.5,
        lineHeight: "16px",
        fontWeight: theme.typography.fontWeights.MEDIUM,
        color: getNewLookTokens(theme).secondary,
      }
    : AUTO_SIZE_TITLE_STYLE;

  const directionField = (
    <>
      <AutoSizeText style={autoSizeTitleStyle}>
        {t("labels.direction")}
      </AutoSizeText>
      {longShort}
    </>
  );
  const leverageFields = (
    <>
      <FieldWrapper>
        <AutoSizeText style={autoSizeTitleStyle}>
          {t("labels.margin_type")}
        </AutoSizeText>
        {leverage?.marginTypeSelector}
      </FieldWrapper>
      <FieldWrapper>
        <AutoSizeText style={autoSizeTitleStyle}>
          {t("labels.multiplier")}
        </AutoSizeText>
        {leverage?.multiplierSelector}
      </FieldWrapper>
    </>
  );

  const getAmountSelectors = () => {
    if (isSignal) {
      return (
        <SignalAmountSelector
          amountValidationMessage={amountValidationMessage}
          invalidTradeAmount={invalidTradeAmount}
        />
      );
    } else
      return (
        <>
          <div>
            <TitleButtonWrapper>
              <FormFieldTitle style={{ margin: 0 }}>
                {amountInputTitle}
              </FormFieldTitle>
              {!showExistingAmount && (
                <Link onClick={onAddExistingAmountClick}>
                  {t("buttons.add_existing_amount")}
                </Link>
              )}
            </TitleButtonWrapper>
            {amountInput}
          </div>
          <ExpandCollapse expanded={showExistingAmount}>
            <TitleButtonWrapper>
              <FormFieldTitle style={{ margin: 0 }}>
                {quantityInputTitle}
              </FormFieldTitle>
              <Link onClick={onRemoveExistingAmountClick}>
                {t("buttons.remove")}
              </Link>
            </TitleButtonWrapper>
            <ExistingAmount>
              <AmountInputWrapper
                onClick={onExistingQuantityClick}
                $isBottomNavigator={isBottomNavigator}
              >
                {quantityInput}
              </AmountInputWrapper>
            </ExistingAmount>
          </ExpandCollapse>
        </>
      );
  };

  const accountComponent = accountSelector && (
    <div key="account">
      <FormFieldTitle>{accountSelectorTitle}</FormFieldTitle>
      {accountSelector}
      {accountError}
    </div>
  );

  const symbolComponent = (
    <div key="symbol">
      <FormFieldTitle
        onClick={openSymbolDrawer}
        style={{ width: "fit-content" }}
      >
        {symbolSelectorTitle}
        <Tooltip title={t("tooltips.view_symbol_details")}>
          <LookIcon
            classic={<StyledOpenInNewIcon />}
            newLook={
              <SymbolLinkIcon
                size={13}
                strokeWidth={NEW_LOOK_ICON_STROKE}
                aria-hidden={true}
              />
            }
          />
        </Tooltip>
      </FormFieldTitle>
      {symbolSelector}
      {symbolError}
      {maxNumTradesForSymbolError}
    </div>
  );

  const advancedFields = (
    <>
      {showTradeType && (
        <TradeTypeWrapper>
          <FormFieldTitle>
            {isSignal ? t("labels.signal_type") : t("labels.trade_type")}
          </FormFieldTitle>
          {tradeType}
        </TradeTypeWrapper>
      )}
      {cancelOnTp}
    </>
  );

  const accountAndSymbol = isSignal
    ? [symbolComponent, accountComponent]
    : [accountComponent, symbolComponent];

  return (
    <LoadingSpinnerOverlay
      isLoading={isLoading || isFetching}
      hideContentWhileLoading={isLoading}
      spinnerMarginTop={100}
      backgroundColor={
        theme.colors[isBottomNavigator ? "layoutBgColor" : "cardBgColor"]
      }
      hideWithOpacity={true}
    >
      <EqualColumnsGrid numColumns={1} rowGap={theme.isNewLook ? 16 : 20}>
        {accountAndSymbol}
        {isSignal && <SubGroupSelector />}
        {getAmountSelectors()}
        {showGeneralOptions && (
          <>
            {longShort && leverage && (
              <div>
                <DirectionFieldsRow
                  direction={<FieldWrapper>{directionField}</FieldWrapper>}
                  fields={leverageFields}
                />
                {(!!marginTypeErrorMessage || !!multiplierErrorMessage) && (
                  <ErrorText>{marginErrorReason}</ErrorText>
                )}
                <ErrorText>{marginTypeErrorMessage}</ErrorText>
                <ErrorText>{multiplierErrorMessage}</ErrorText>
              </div>
            )}
            {tradeType && cancelOnTp && (
              <div>
                {theme.isNewLook ? (
                  <AdvancedToggle
                    type="button"
                    aria-expanded={advancedExpanded}
                    onClick={() => setAdvancedExpanded(!advancedExpanded)}
                  >
                    {t("labels.advanced_optional")}
                    <AdvancedChevron
                      size={16}
                      strokeWidth={NEW_LOOK_ICON_STROKE}
                      $expanded={advancedExpanded}
                    />
                  </AdvancedToggle>
                ) : (
                  <>
                    <FormDivider style={{ marginTop: 0 }} />
                    <AdvancedTitle
                      onClick={() => setAdvancedExpanded(!advancedExpanded)}
                    >
                      {t("labels.advanced_optional")}
                      <Arrow $expanded={advancedExpanded} />
                    </AdvancedTitle>
                  </>
                )}
                <ExpandCollapse expanded={advancedExpanded}>
                  {theme.isNewLook ? (
                    <AdvancedBody>{advancedFields}</AdvancedBody>
                  ) : (
                    advancedFields
                  )}
                </ExpandCollapse>
              </div>
            )}
          </>
        )}
      </EqualColumnsGrid>
      {confirmModalContextHolder}
      {leverageWarningModal}
    </LoadingSpinnerOverlay>
  );
};

export default General;

const AUTO_SIZE_TITLE_STYLE = {
  marginBottom: 8,
};

const StyledOpenInNewIcon = styled(OpenInNewIcon)`
  margin-left: 8px;
  width: 10px;
  fill: ${(p) => p.theme.colors.primaryGrey};
`;
const TitleButtonWrapper = styled(Flex)`
  margin-bottom: 8px;
  ${(p) =>
    p.theme.isNewLook &&
    `
    > a {
      font-size: 12.5px;
      line-height: 16px;
      font-weight: ${p.theme.typography.fontWeights.MEDIUM};
    }
  `}
`;
const AdvancedTitle = styled(Title).attrs({ level: 4, type: "secondary" })`
  margin-bottom: 16px;
  display: flex;
  align-items: center;
`;
const TradeTypeWrapper = styled.div`
  margin-bottom: 16px;
  width: 50%;
  ${(p) =>
    p.theme.isNewLook &&
    `
    width: 100%;
    margin-bottom: 16px;
  `}
`;
const SymbolLinkIcon = styled(ExternalLink)`
  color: ${(p) => p.theme.colors.secondaryText};
  transition: color 150ms;
  &:hover {
    color: ${(p) => p.theme.colors.primaryBlue};
  }
`;
const AdvancedToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  /* 16px from the hairline, like the grid's 16px above it; the fields get
     their own 16px so a collapsed toggle sits centred between hairlines. */
  padding: 16px 0 0;
  border: none;
  border-top: 1px solid ${(p) => p.theme.colors.cardBorderColor};
  background: transparent;
  font: inherit;
  font-size: 14px;
  line-height: 20px;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  color: ${(p) => p.theme.colors.titleColor};
  cursor: pointer;
  &:hover svg {
    color: ${(p) => p.theme.colors.primaryText};
  }
`;
const AdvancedBody = styled.div`
  padding-top: 16px;
`;
const AdvancedChevron = styled(ChevronDown)<{ $expanded?: boolean }>`
  color: ${(p) => p.theme.colors.secondaryText};
  transform: rotate(${(p) => (p.$expanded ? 180 : 0)}deg);
  transition: transform 200ms ease, color 150ms;
`;
const ExistingAmount = styled.div`
  display: block;
  position: relative;
`;
const AmountInputWrapper = styled.div<{
  onClick?: () => void;
  $isBottomNavigator?: boolean;
}>`
  ${(p) =>
    p.onClick &&
    `
    &:after {
      opacity: 0.6;
      content: "";
      display: block;
      height: 100%;
      width: 100%;
      top: 0;
      left: 0;
      position: absolute;
      background-color: ${
        p.$isBottomNavigator
          ? p.theme.colors.layoutBgColor
          : p.theme.colors.cardBgColor
      };
      cursor: pointer;
    }
  `}
`;
const FieldWrapper = styled(Flex)`
  flex-direction: column;
`;
